import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  OptimizelyDecideOption,
  OptimizelyDecision,
} from '@optimizely/react-sdk'
import { browserLogger } from 'lib/Logger/browserLogs'

import { isProduction } from '../../src/constants'
import { optimizelyInstance } from './optimizelyInstance'
import {
  cacheDecision,
  getCachedDecision,
} from './utils/getCachedDecisionResponse'

const DEFAULT_VALUES = {
  variables: {},
  variationKey: 'off',
  enabled: false,
  flagKey: '',
  reasons: [],
  ruleKey: '',
  userContext: { id: '' },
}

interface Feature {
  name: string
  fallbackVariation: string
  bypassActivate?: boolean
  includeReasons?: boolean
}

export const useDecisionForFlag = ({
  name,
  fallbackVariation,
  bypassActivate = false,
  includeReasons = false,
}: Feature) => {
  const [isLoading, setLoading] = useState(true)
  const [decision, setDecision] = useState<OptimizelyDecision | null>(null)
  const instance = useMemo(() => optimizelyInstance(), [])

  const handleDecision = (decision: OptimizelyDecision) => {
    setDecision(decision)
    setLoading(false)
  }

  const decisionOptions = useMemo(() => {
    return !isProduction && includeReasons
      ? [OptimizelyDecideOption.INCLUDE_REASONS]
      : []
  }, [includeReasons])

  const handleInstanceDecision = useCallback(async () => {
    if (!instance) return
    try {
      await instance.onReady()
      const decision = instance.decide(name, decisionOptions)
      handleDecision(decision)
      cacheDecision(name, decision)
    } catch (error) {
      browserLogger.error('Error getting decision for flag', {
        error,
        flagKey: name,
      })
      handleDecision(DEFAULT_VALUES)
    }
  }, [instance, name, decisionOptions])

  useEffect(() => {
    if (bypassActivate) {
      handleDecision({
        ...DEFAULT_VALUES,
        variationKey: fallbackVariation,
        enabled: true,
        flagKey: name,
      })
      return
    }

    const cachedDecision = getCachedDecision(name)
    if (cachedDecision) {
      handleDecision(cachedDecision)
      return
    }

    handleInstanceDecision()
  }, [bypassActivate, fallbackVariation, handleInstanceDecision, name])

  const decisionState = useMemo(
    () => ({
      isEnabled: decision?.enabled,
      variation: decision?.variationKey || fallbackVariation,
      variables: decision?.variables,
      isLoading,
      reasons: decision?.reasons,
    }),
    [decision, fallbackVariation, isLoading]
  )

  return decisionState
}
